/**
 * Sandbox — the Pilot Sandbox view, end-to-end.
 *
 * Phase 3 of the Oatmeal rebuild (`docs/eng/pilot-oatmeal-rebuild.md`)
 * folds the prior `SandboxSections.tsx` JSX in-line as the default
 * export of this file — single-view-single-file per the rebuild's
 * invariant. The named React-owned building blocks
 * (SandboxKnobGrid, SandboxRaceLanes, SandboxStatRow,
 * SandboxFloatingFab) remain at the top; they're now local helpers
 * the default `Sandbox` composition mounts, plus their exports are
 * kept in case future composition needs them again.
 *
 * Section composition note (carries from Phase 2): vendored Oatmeal
 * *page sections* (`features-with-large-demo`, `stats-three-column`,
 * etc.) don't fit this view either — Sandbox's body is largely
 * domain-specific (engine-painted stat rows, custom knob sliders,
 * race-lane SVG viz, custom FAB). Composition still uses Oatmeal
 * *element* primitives (`Container`, `Subheading`, `Text`,
 * `Eyebrow`, `Button`) where they fit; the rest stays as custom
 * paint with engine bindings preserved.
 *
 * Knob grid, race-lane SVG painting, stat row, FAB, hero wins,
 * today's tax, step breakdown, distribution headline, failure-rate
 * bar, histogram, portfolio rollup, jump orb, TLDR strip, and AI
 * actions list are all React components in this file — they
 * subscribe directly to useSimWorker results + PilotContext knobs.
 *
 * Sub-components (in-file; only Sandbox uses them):
 *
 *   - <KnobGrid>     — one slider per `KnobMeta` in `lib/pilot/knobs.ts`,
 *                      with audit #11 ARIA fix (separated `aria-label`,
 *                      `aria-valuetext`, `aria-describedby`).
 *   - <RaceLanes>    — Monte Carlo trial visualization. Subscribes to
 *                      the typed sim-worker via `useSimWorker` and
 *                      paints SVG into ref-owned `<svg>` nodes (the
 *                      canvas-pixels exception to the React-owns-DOM
 *                      rule).
 *   - <StatRow>      — process meta line. Inline separators carried
 *                      with their preceding stat so wrap behaves
 *                      (audit #13 fix).
 *   - <SandboxFab>   — floating "↻ Try another process" button.
 *                      Visibility driven by React state +
 *                      IntersectionObserver scoped to the Sandbox
 *                      `<section id="notebook">` (audit #8 fix).
 */

import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type {ParamId} from '../../lib/pilot/params';
import {KNOBS, type KnobMeta} from '../../lib/pilot/knobs';
import {PROCESSES, pickPortfolio, INDUSTRIES, SIZES} from '../../lib/pilot/processes';
import type {ProcessDef, Intake} from '../../lib/pilot/processes';
import {
  replayTrial,
  runMonteCarloSummary,
  type ReplayResult,
} from '../../lib/pilot/simulation';
import type {TraceEntry} from '../../lib/pilot/sim-engine';
import {usePilot, type KnobValues} from './PilotContext';
import {useSimWorker} from '../../hooks/useSimWorker';
import {useCountUp} from '../../hooks/useCountUp';
import {categoryGlyphSvg} from '../../lib/pilot/categoryGlyph';
import {outcomeNounFor, aiActionsFor} from '../../lib/pilot/processes';
import {fmtUSD, fteEquivalent} from '../../lib/pilot/format';
import {PROCS_PER_YEAR} from '../../lib/pilot/processes';
import {buildSandboxShareUrl} from '../../lib/pilot/url-state';
import {renderHistogram} from '../../lib/pilot/story-render';
import {Container} from '../../oatmeal/elements/container';
import {Subheading} from '../../oatmeal/elements/subheading';
import {Text} from '../../oatmeal/elements/text';
import {Eyebrow} from '../../oatmeal/elements/eyebrow';
import {Button} from '../../oatmeal/elements/button';
import {Tt, Skel, Stat} from './_helpers';
import SceneDots, {SceneDescriptor} from './SceneDots';
import {useScrollSpy} from '../../hooks/useScrollSpy';
import {
  PILOT_PROCESS_SELECTED_EVENT,
  PILOT_OPEN_TALK_MODAL_EVENT,
  PILOT_SET_VIEW_MODE_EVENT,
} from '../../lib/pilot/events';

/* ============================================================
   KnobGrid
   Audit #11 (slider aria-label run-on) is killed structurally:
     - `aria-label`        → clean knob name, nothing else
     - `aria-valuetext`    → formatted live value
     - `aria-describedby`  → hint + optional leverage badge id
   The leverage badge text stays out of the <label> so screen
   readers don't concatenate "Loaded hourly cost high-leverage
   $150/hr" into one accessible name.
   ============================================================ */
function KnobGrid({proc}: {proc: ProcessDef | null}) {
  const {knobs, setKnob, resetKnobs} = usePilot();

  // Per the May 2026 review: too many dials and unclear which ones
  // apply end-to-end vs per-handoff. Split into two labelled groups so
  // the visitor sees the scope at a glance.
  const processKnobs = KNOBS.filter((k) => k.scope === 'process');
  const stepKnobs = KNOBS.filter((k) => k.scope === 'step');

  return (
    <>
      <div className="nb-knob-group flex flex-col gap-3">
        <div className="flex items-baseline gap-3">
          <span className="font-mono text-xs/5 uppercase tracking-widest text-taupe-500">
            Process-wide
          </span>
          <span className="text-xs/5 text-taupe-600 dark:text-taupe-500">
            Apply across the whole run.
          </span>
        </div>
        <div className="nb-knob-grid grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {processKnobs.map((k) => (
            <KnobRow
              key={k.id}
              meta={k}
              value={knobs[k.id]}
              onChange={(v) => setKnob(k.id, v)}
            />
          ))}
        </div>
      </div>
      <div className="nb-knob-group mt-6 flex flex-col gap-3">
        <div className="flex items-baseline gap-3">
          <span className="font-mono text-xs/5 uppercase tracking-widest text-taupe-500">
            Per-handoff
          </span>
          <span className="text-xs/5 text-taupe-600 dark:text-taupe-500">
            Apply at every step in the run.
          </span>
        </div>
        <div className="nb-knob-grid grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {stepKnobs.map((k) => (
            <KnobRow
              key={k.id}
              meta={k}
              value={knobs[k.id]}
              onChange={(v) => setKnob(k.id, v)}
            />
          ))}
        </div>
      </div>
      <div className="nb-knobs-footer flex flex-wrap items-center gap-4 mt-6">
        <button
          type="button"
          id="run-btn-reset"
          className="btn-secondary nb-rerun rounded-full border border-taupe-300 bg-white px-5 py-2 text-sm/6 font-medium text-taupe-950 hover:bg-taupe-50 dark:border-taupe-700 dark:bg-taupe-900 dark:text-white"
          onClick={() => {
            resetKnobs();
            void proc;
          }}
        >
          ↺ Reset to defaults
        </button>
        <a
          href="#nb-impact"
          className="nb-back-to-results text-sm/7 font-medium text-taupe-700 underline underline-offset-4 dark:text-taupe-400"
        >
          ↓ See your results
        </a>
      </div>
    </>
  );
}

function KnobRow({
  meta,
  value,
  onChange,
}: {
  meta: KnobMeta;
  value: number;
  onChange: (v: number) => void;
}) {
  // Unique ids so multiple Sandboxes (or React Strict-Mode double mount)
  // never collide aria pointers. useId() gives us a stable per-instance
  // string with no DOM-collision risk.
  const reactId = useId();
  const inputId = `inp-${meta.id}-${reactId}`;
  const hintId = `hint-${meta.id}-${reactId}`;
  const badgeId = `badge-${meta.id}-${reactId}`;
  const valueText = meta.fmt(value);
  // describedby joins hint + (optional) leverage badge so assistive
  // tech reads them after the slider's name+value, not concatenated
  // into the name.
  const describedBy = meta.accent ? `${hintId} ${badgeId}` : hintId;

  // Outer container uses Tailwind utilities instead of the legacy
  // `.knob` / `.knob-accent` CSS rules from `pilot-engine.css` —
  // those rules were tied to JS-generated DOM and got deleted in
  // Phase 5 (docs/eng/pilot-react-engine.md). The class names stay
  // as data hooks for E2E tests but no CSS depends on them.
  const containerClasses = meta.accent
    ? 'knob knob-accent flex flex-col gap-2 rounded-2xl border border-taupe-700 bg-taupe-50 p-4 dark:border-taupe-300 dark:bg-taupe-900'
    : 'knob flex flex-col gap-2 rounded-2xl border border-taupe-200 bg-white p-4 dark:border-taupe-800 dark:bg-taupe-900';
  return (
    <div className={containerClasses}>
      <label
        htmlFor={inputId}
        className="flex flex-wrap items-baseline justify-between gap-2 text-[0.8125rem] font-medium text-taupe-700 dark:text-taupe-300"
      >
        <span className="knob-label-text">{meta.label}</span>
        <span className="val font-mono text-sm font-semibold text-taupe-950 dark:text-white">
          {valueText}
        </span>
      </label>
      {meta.accent ? (
        <span
          id={badgeId}
          className="knob-accent-badge inline-flex w-fit rounded-full bg-taupe-950 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-taupe-50 dark:bg-taupe-100 dark:text-taupe-950"
        >
          high-leverage
        </span>
      ) : null}
      <input
        id={inputId}
        type="range"
        min={meta.min}
        max={meta.max}
        step={meta.step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        aria-label={meta.label}
        aria-valuetext={valueText}
        aria-describedby={describedBy}
        className="w-full accent-taupe-950 dark:accent-taupe-100"
      />
      <div id={hintId} className="hint text-xs/5 text-taupe-600 dark:text-taupe-400">
        {meta.hint}
      </div>
    </div>
  );
}

/* ============================================================
   StatRow — "N participants · M handoffs · K organizations".
   Audit #13 (separator wrap): each separator dot is carried
   inside the same inline-flex span as its preceding stat so
   when the row wraps, the dot never leads a new line.
   ============================================================ */
function StatRow({proc}: {proc: ProcessDef | null}) {
  if (!proc) {
    return (
      <div
        id="nb-proc-meta"
        className="nb-hr-stats mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm/6 text-taupe-700 dark:text-taupe-400"
      />
    );
  }
  const orgWord = proc.orgs.length === 1 ? 'organization' : 'organizations';
  return (
    <div
      id="nb-proc-meta"
      className="nb-hr-stats mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm/6 text-taupe-700 dark:text-taupe-400"
    >
      <span className="nb-stat inline-flex items-baseline gap-2">
        <span>
          <b>{proc.parties.length}</b> participants
        </span>
        <span className="sep text-taupe-400" aria-hidden="true">
          ·
        </span>
      </span>
      <span className="nb-stat inline-flex items-baseline gap-2">
        <span>
          <b>{proc.steps.length}</b> handoffs
        </span>
        <span className="sep text-taupe-400" aria-hidden="true">
          ·
        </span>
      </span>
      <span className="nb-stat inline-flex items-baseline gap-2">
        <span>
          <b>{proc.orgs.length}</b> {orgWord}
        </span>
      </span>
    </div>
  );
}

/* ============================================================
   RaceLanes — Monte Carlo trial visualization for human vs AI.
   The SVG painting itself is imperative inside a useEffect with
   refs (canvas-pixels exception). React owns the <svg> nodes and
   their layout; the effect mutates only inside the refs.
   ============================================================ */
interface LaneNode {
  id: string;
  name: string;
  org: string;
  x: number;
  y: number;
}

interface LaneLayout {
  nodes: LaneNode[];
  hub: {x: number; y: number} | null;
}

function layoutLaneNodes(
  parties: ProcessDef['parties'],
  w: number,
  h: number,
  mode: 'human' | 'ai',
): LaneLayout {
  const cx = w / 2;
  const cy = h / 2 + 10;
  const r = Math.min(w, h) * 0.4;
  const N = parties.length || 1;
  const nodes: LaneNode[] = parties.map((p, i) => {
    const ang = -Math.PI / 2 + (i / N) * Math.PI * 2;
    return {
      id: p.id,
      name: p.name,
      org: p.org,
      x: cx + Math.cos(ang) * r,
      y: cy + Math.sin(ang) * r,
    };
  });
  const hub = mode === 'ai' ? {x: cx, y: cy} : null;
  return {nodes, hub};
}

function tracePartySet(trace: TraceEntry[]): Set<string> {
  const set = new Set<string>();
  for (const step of trace) {
    set.add(step.from);
    set.add(step.to);
  }
  return set;
}

function readThemeColors(): Record<string, string> {
  if (typeof document === 'undefined') {
    return {
      human: '#f59f4a',
      ai: '#6dd6ff',
      brand: '#ff5d8f',
      dim: '#b9c2d8',
      muted: '#8896b3',
    };
  }
  const cs = getComputedStyle(document.documentElement);
  const v = (name: string, fb: string) =>
    cs.getPropertyValue(name).trim() || fb;
  return {
    human: v('--human', '#f59f4a'),
    ai: v('--ai', '#6dd6ff'),
    brand: v('--brand', '#ff5d8f'),
    dim: v('--text-dim', '#b9c2d8'),
    muted: v('--muted', '#8896b3'),
  };
}

function escapeXml(s: string): string {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function wrapLabel(raw: string): string[] {
  if (raw.length <= 22) return [raw];
  const slashIdx = raw.lastIndexOf(' / ', 22);
  if (slashIdx > 6) return [raw.slice(0, slashIdx).trim(), raw.slice(slashIdx).trim()];
  const spaceIdx = raw.lastIndexOf(' ', 22);
  if (spaceIdx > 6) return [raw.slice(0, spaceIdx).trim(), raw.slice(spaceIdx + 1).trim()];
  return [raw.slice(0, 21) + '…'];
}

interface LanePaintArgs {
  svg: SVGSVGElement;
  proc: ProcessDef;
  layout: LaneLayout;
  trace: TraceEntry[];
  totalHrs: number;
  mode: 'human' | 'ai';
  clockEl: HTMLElement | null;
  durationMs: number;
}

function paintLaneStatic(
  svg: SVGSVGElement,
  proc: ProcessDef,
  layout: LaneLayout,
  involved: Set<string>,
): void {
  const colors = readThemeColors();
  const orgs = Object.fromEntries(proc.orgs.map((o) => [o.id, o]));
  const hubColor = colors.brand;

  const nodeGroup = layout.nodes
    .map((n) => {
      const o = orgs[n.org] || {color: colors.muted};
      const lines = wrapLabel(n.name);
      const uninvolved = !involved.has(n.id);
      const cls = uninvolved ? 'lane-node lane-node-uninvolved' : 'lane-node';
      const textBody =
        lines.length === 1
          ? escapeXml(lines[0])
          : `<tspan x="${n.x}" dy="0">${escapeXml(lines[0])}</tspan><tspan x="${n.x}" dy="1.15em">${escapeXml(lines[1])}</tspan>`;
      return `
        <g class="${cls}" data-id="${escapeXml(n.id)}">
          <circle cx="${n.x}" cy="${n.y}" r="18" fill="${o.color}22" stroke="${o.color}" stroke-width="2"/>
          <text x="${n.x}" y="${n.y + 36}" fill="${colors.dim}" font-size="12" font-weight="500" text-anchor="middle">${textBody}</text>
        </g>
      `;
    })
    .join('');

  const hub = layout.hub
    ? `
        <g>
          <circle cx="${layout.hub.x}" cy="${layout.hub.y}" r="28" fill="${hubColor}22" stroke="${hubColor}" stroke-width="2"/>
          <text x="${layout.hub.x}" y="${layout.hub.y + 8}" fill="${hubColor}" font-size="26" text-anchor="middle">🐙</text>
        </g>
      `
    : '';

  let spokes = '';
  if (layout.hub) {
    spokes = layout.nodes
      .map((n) => {
        const uninvolved = !involved.has(n.id);
        const stroke = `${hubColor}${uninvolved ? '1F' : '44'}`;
        return `<line x1="${layout.hub!.x}" y1="${layout.hub!.y}" x2="${n.x}" y2="${n.y}" stroke="${stroke}" stroke-width="1"/>`;
      })
      .join('');
  }

  // The painted structure mirrors the legacy render.js output so any
  // CSS rule keyed off `.lane-node` / `.lane-node-uninvolved` keeps
  // working without edit. We own the svg via the React ref; this
  // assignment is the canvas-exception carve-out for SVG.
  svg.innerHTML = `
    ${spokes}
    <g class="lane-path"></g>
    ${nodeGroup}
    ${hub}
    <g class="lane-active"></g>
    <g class="lane-token"></g>
  `;
}

function animateLane(args: LanePaintArgs): () => void {
  const {svg, layout, trace, totalHrs, mode, clockEl, durationMs} = args;
  // Capture into locals after the null-guard so TypeScript narrows
  // them inside the closure below.
  const tokenG: SVGGElement | null = svg.querySelector<SVGGElement>('.lane-token');
  const activeG: SVGGElement | null = svg.querySelector<SVGGElement>('.lane-active');
  const pathG: SVGGElement | null = svg.querySelector<SVGGElement>('.lane-path');
  if (!tokenG || !trace.length) return () => undefined;
  const tokenGroup: SVGGElement = tokenG;
  const colors = readThemeColors();
  const humanColor = colors.human;
  const aiColor = colors.ai;
  const partyPos = (id: string) =>
    layout.nodes.find((n) => n.id === id) || layout.nodes[0];
  const start = performance.now();
  let rafId = 0;
  let cancelled = false;

  function frame(now: number) {
    if (cancelled) return;
    const tRaw = (now - start) / durationMs;
    const t = Math.min(1, tRaw);
    let cumStart = 0;
    let cumEnd = 0;
    let idx = 0;
    for (let i = 0; i < trace.length; i++) {
      cumEnd = cumStart + trace[i].lat;
      if (t * totalHrs <= cumEnd) {
        idx = i;
        break;
      }
      cumStart = cumEnd;
      idx = i;
    }
    const step = trace[idx];
    const localT =
      totalHrs > 0
        ? Math.min(1, (t * totalHrs - cumStart) / Math.max(0.001, step.lat))
        : 1;
    const fromNode = partyPos(step.from);
    const toNode = partyPos(step.to);

    let x: number;
    let y: number;
    if (mode === 'ai' && layout.hub) {
      if (localT < 0.5) {
        const u = localT / 0.5;
        x = fromNode.x + (layout.hub.x - fromNode.x) * u;
        y = fromNode.y + (layout.hub.y - fromNode.y) * u;
      } else {
        const u = (localT - 0.5) / 0.5;
        x = layout.hub.x + (toNode.x - layout.hub.x) * u;
        y = layout.hub.y + (toNode.y - layout.hub.y) * u;
      }
    } else {
      x = fromNode.x + (toNode.x - fromNode.x) * localT;
      y = fromNode.y + (toNode.y - fromNode.y) * localT;
    }

    if (mode === 'human' && pathG) {
      let pathSvg = '';
      for (let i = 0; i < idx; i++) {
        const s = trace[i];
        const f = partyPos(s.from);
        const tn = partyPos(s.to);
        pathSvg += `<line x1="${f.x}" y1="${f.y}" x2="${tn.x}" y2="${tn.y}" stroke="${humanColor}" stroke-width="1.5" stroke-opacity="0.55" stroke-linecap="round"/>`;
      }
      pathSvg += `<line x1="${fromNode.x}" y1="${fromNode.y}" x2="${x}" y2="${y}" stroke="${humanColor}" stroke-width="2" stroke-opacity="0.9" stroke-linecap="round"/>`;
      pathG.innerHTML = pathSvg;
    }

    const color = mode === 'ai' ? aiColor : humanColor;
    tokenGroup.innerHTML = `
      <circle cx="${x}" cy="${y}" r="6" fill="${color}" />
      <circle cx="${x}" cy="${y}" r="11" fill="${color}33" />
    `;
    if (activeG) {
      const labelDisplay = escapeXml(step.label).replace(/-/g, '‑');
      activeG.innerHTML = `
        <text x="300" y="24" text-anchor="middle" fill="${color}" font-size="15" font-weight="700">
          ${labelDisplay}
        </text>
      `;
    }
    if (clockEl) clockEl.textContent = `Day ${((t * totalHrs) / 24).toFixed(1)}`;

    if (tRaw < 1) rafId = requestAnimationFrame(frame);
    else if (clockEl) clockEl.textContent = `Day ${(totalHrs / 24).toFixed(1)}`;
  }
  rafId = requestAnimationFrame(frame);
  return () => {
    cancelled = true;
    if (rafId) cancelAnimationFrame(rafId);
  };
}

const SANDBOX_SIM_TOTAL = 10000;

function RaceLanes({proc}: {proc: ProcessDef | null}) {
  const {knobs} = usePilot();
  const humanSvgRef = useRef<SVGSVGElement | null>(null);
  const aiSvgRef = useRef<SVGSVGElement | null>(null);
  const humanClockRef = useRef<HTMLDivElement | null>(null);
  const aiClockRef = useRef<HTMLDivElement | null>(null);
  const [simIdx, setSimIdx] = useState<number>(1);

  // Compute the replayed trial deterministically from `simIdx + knobs`.
  // `replayTrial` is pure given (proc, knobs, idx) so memoization is
  // safe even across worker recomputes.
  const replay: ReplayResult | null = useMemo(() => {
    if (!proc) return null;
    return replayTrial(proc, knobs as unknown as Record<string, number>, simIdx);
  }, [proc, knobs, simIdx]);

  // Paint + animate effect. Re-runs whenever the chosen trial changes
  // (new simIdx, new knobs, new process). Cleanup cancels any rAF loop
  // before the next paint starts.
  useLayoutEffect(() => {
    if (!proc || !replay) return;
    const w = 600;
    const h = 540;
    const humanLayout = layoutLaneNodes(proc.parties, w, h, 'human');
    const aiLayout = layoutLaneNodes(proc.parties, w, h, 'ai');

    if (humanSvgRef.current) {
      paintLaneStatic(
        humanSvgRef.current,
        proc,
        humanLayout,
        tracePartySet(replay.human.trace),
      );
    }
    if (aiSvgRef.current) {
      paintLaneStatic(
        aiSvgRef.current,
        proc,
        aiLayout,
        tracePartySet(replay.ai.trace),
      );
    }
    const maxHrs = Math.max(replay.human.hours, replay.ai.hours, 1);
    const totalMs = 8000;
    const factor = totalMs / maxHrs;
    const cancels: Array<() => void> = [];
    if (humanSvgRef.current) {
      cancels.push(
        animateLane({
          svg: humanSvgRef.current,
          proc,
          layout: humanLayout,
          trace: replay.human.trace,
          totalHrs: replay.human.hours,
          mode: 'human',
          clockEl: humanClockRef.current,
          durationMs: replay.human.hours * factor,
        }),
      );
    }
    if (aiSvgRef.current) {
      cancels.push(
        animateLane({
          svg: aiSvgRef.current,
          proc,
          layout: aiLayout,
          trace: replay.ai.trace,
          totalHrs: replay.ai.hours,
          mode: 'ai',
          clockEl: aiClockRef.current,
          durationMs: replay.ai.hours * factor,
        }),
      );
    }
    return () => {
      for (const c of cancels) c();
    };
  }, [proc, replay]);

  const pickRandomTrial = useCallback(() => {
    setSimIdx(1 + Math.floor(Math.random() * SANDBOX_SIM_TOTAL));
  }, []);

  return (
    <div className="nb-race-lanes flex flex-col gap-3">
      <div
        className="nb-sim-chrome flex flex-col gap-3 rounded-2xl border border-taupe-200 bg-white p-4 dark:border-taupe-800 dark:bg-taupe-900"
        id="nb-sim-chrome"
      >
        <div
          className="nb-sim-caption flex flex-wrap items-baseline gap-2 text-sm/6 text-taupe-700 dark:text-taupe-400"
          id="nb-sim-caption"
        >
          <span className="nb-sim-label">Simulation</span>
          <strong
            id="nb-sim-num"
            className="nb-sim-num font-display text-2xl/8 text-taupe-950 tabular-nums dark:text-white"
          >
            {simIdx.toLocaleString()}
          </strong>
          <span className="nb-sim-label">
            of 10,000. Drag to watch another
          </span>
        </div>
        <div className="nb-sim-slider-wrap flex flex-col gap-2">
          <input
            id="nb-sim-slider"
            className="nb-sim-slider w-full accent-taupe-950 dark:accent-taupe-100"
            type="range"
            min={1}
            max={SANDBOX_SIM_TOTAL}
            value={simIdx}
            step={1}
            aria-label="Simulation trial selector"
            aria-valuetext={`Trial ${simIdx} of ${SANDBOX_SIM_TOTAL}`}
            onChange={(e) => setSimIdx(parseInt(e.target.value, 10) || 1)}
          />
        </div>
      </div>
      <div className="lane-row grid grid-cols-1 items-stretch gap-4 md:grid-cols-[1fr_auto_1fr]">
        <RaceLane
          variant="human"
          title="Today: human-routed"
          caption="Each handoff waits for the next person. Drops, OOO, and rework compound."
          svgRef={(el) => {
            humanSvgRef.current = el;
          }}
          clockRef={(el) => {
            humanClockRef.current = el;
          }}
          onReplay={pickRandomTrial}
        />
        <div className="vs flex items-center justify-center font-display text-2xl/none text-taupe-500">
          VS
        </div>
        <RaceLane
          variant="ai"
          title="With Planapus"
          caption="All handoffs route through one orchestrator. Always-on, never OOO."
          svgRef={(el) => {
            aiSvgRef.current = el;
          }}
          clockRef={(el) => {
            aiClockRef.current = el;
          }}
          onReplay={pickRandomTrial}
        />
      </div>
    </div>
  );
}

function RaceLane({
  variant,
  title,
  caption,
  svgRef,
  clockRef,
  onReplay,
}: {
  variant: 'human' | 'ai';
  title: string;
  caption?: string;
  // React 18's stricter ref typing rejects RefObject<T | null> on ref={…}.
  // Callback refs forward through cleanly and let the parent hold the
  // RefObject pattern it actually wants.
  svgRef: (el: SVGSVGElement | null) => void;
  clockRef: (el: HTMLDivElement | null) => void;
  onReplay: () => void;
}) {
  return (
    <div
      className={`lane flex flex-col gap-3 rounded-2xl border border-taupe-200 bg-white p-5 dark:border-taupe-800 dark:bg-taupe-900 ${variant}`}
    >
      <div className="lane-header flex items-baseline justify-between border-b border-taupe-200 pb-3 dark:border-taupe-800">
        <div className="title text-sm/6 font-medium text-taupe-950 dark:text-white">
          {title}
        </div>
        <div
          ref={clockRef}
          className="clock font-display text-base/6 text-taupe-700 tabular-nums dark:text-taupe-400"
        >
          Day 0.0
        </div>
      </div>
      <svg
        ref={svgRef}
        viewBox="0 0 600 540"
        preserveAspectRatio="xMidYMid meet"
        className="w-full cursor-pointer"
        onClick={onReplay}
        role="img"
        aria-label={`${title} lane. Click to replay.`}
      />
      {caption ? (
        <p className="lane-caption text-xs/5 text-taupe-600 dark:text-taupe-500">
          {caption}
        </p>
      ) : null}
    </div>
  );
}

/* ============================================================
   SandboxFab — floating "↻ Try another process" button.
   Audit #8 (FAB cross-view leak): visibility is React-state
   driven (viewMode + scroll position inside #notebook), not
   CSS rules against body classes that can stack with other
   modes. The IntersectionObserver here is scoped to a single
   target node and unmounts cleanly when viewMode changes.
   ============================================================ */
function SandboxFab({
  onClick,
  hasProcess,
}: {
  onClick: () => void;
  hasProcess: boolean;
}) {
  const {viewMode} = usePilot();
  const [inSandboxScroll, setInSandboxScroll] = useState<boolean>(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (typeof IntersectionObserver === 'undefined') return;
    if (viewMode !== 'sandbox' && viewMode !== 'story') {
      setInSandboxScroll(false);
      return;
    }
    const target = document.getElementById('notebook');
    if (!target) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          const vh = window.innerHeight || 1;
          // When more than the top third of the notebook is on screen,
          // count it as "in sandbox scroll" (same threshold the
          // sandbox-side dot navigator's body-class IntersectionObserver
          // in pilot-engine.js uses, so the FAB and the side dots
          // appear at the same scroll position).
          const inSandbox = e.boundingClientRect.top < vh * 0.33;
          setInSandboxScroll(inSandbox);
        }
      },
      {threshold: 0, rootMargin: '-33% 0px -67% 0px'},
    );
    io.observe(target);
    return () => io.disconnect();
  }, [viewMode]);

  // Visible only when:
  //  - viewMode is story/sandbox (NOT pitch/demo/vision — audit #8 fix)
  //  - the visitor has actually scrolled into the notebook
  //  - a process is picked (otherwise FAB above a hidden notebook overlaps
  //    Story's "Continue ↓" pill)
  const visible =
    (viewMode === 'sandbox' || viewMode === 'story') &&
    inSandboxScroll &&
    hasProcess;

  if (!visible) return null;
  return (
    <button
      id="sandbox-fab"
      type="button"
      className="sandbox-fab fixed bottom-6 right-6 z-20 inline-flex items-center gap-2 rounded-full bg-taupe-950 px-5 py-3 text-sm/6 font-medium text-white shadow-lg hover:bg-taupe-800 dark:bg-taupe-100 dark:text-taupe-950 dark:hover:bg-white"
      aria-label="Try another process"
      onClick={onClick}
    >
      <span className="sandbox-fab-icon">↻</span>
      <span>Try another process</span>
    </button>
  );
}

/* ============================================================
   Composition exports — consumed by SandboxSections.tsx
   ============================================================ */

export interface SandboxPanelsProps {
  proc: ProcessDef | null;
}

/**
 * KnobGrid + sim-worker trigger combo. Mount inside the existing
 * `<section id="nb-knobs">` shell so the surrounding eyebrow / heading
 * / link copy stay where the rest of the page expects them.
 */
export function SandboxKnobGrid({proc}: SandboxPanelsProps) {
  const {knobs, intake} = usePilot();
  const {run} = useSimWorker();

  // Whenever the picked process, intake, or any knob changes, kick off
  // a new worker run. The worker already debounces by cancelling
  // in-flight runs in `useSimWorker.run()`, so a rapid slider drag
  // posts many cancels and one final compute.
  useEffect(() => {
    if (!proc) return;
    run({
      proc,
      // Intake is React-owned (PilotContext) since F6 of the Oatmeal
      // rebuild. Pass it through so worker filtering matches what the
      // visitor configured in Story scene 1.5.
      intake,
      params: knobs as unknown as Record<string, number>,
      n: 10000,
      winProbN: 1500,
      breakdownN: 200,
    });
  }, [proc, intake, knobs, run]);

  return <KnobGrid proc={proc} />;
}

/** Race lanes section content — paints into ref-owned SVG nodes. */
export function SandboxRaceLanes({proc}: SandboxPanelsProps) {
  return <RaceLanes proc={proc} />;
}

/** StatRow — the "N participants · M handoffs · K organizations" line. */
export function SandboxStatRow({proc}: SandboxPanelsProps) {
  return <StatRow proc={proc} />;
}

/* ============================================================
   SandboxHeroWins / SandboxTodaysTax — Phase C of the imperative-
   renderer retirement (docs/eng/pilot-engine-imperative-retirement.md).
   React replacements for renderHeroWins (R3) + renderTodaysTax (R4)
   from story-render.js. Compute their numbers from the sim worker's
   `results.human` / `results.ai` and the live `hourlyRate` knob;
   useCountUp drives the visual count-up animation when the
   surrounding section enters the viewport.
   ============================================================ */

const fmtHours = (v: number) => `${Math.round(v).toLocaleString()} hrs`;
const fmtDeals = (v: number) => (v >= 10 ? `${Math.round(v)}` : v.toFixed(1));
const fmtScaleHours = (v: number) => `${Math.round(v).toLocaleString()} hrs`;
const fmtScaleDeals = (v: number) =>
  v >= 10 ? `${Math.round(v).toLocaleString()}` : v.toFixed(1);

const PORTFOLIO_TARGET = 10;
const PORTFOLIO_TRIALS = 1500;

function contextLabelFromIntake(intake: Partial<Intake> | null | undefined): string {
  if (!intake) return '';
  const parts: string[] = [];
  if (intake.industry) {
    const i = INDUSTRIES.find((x) => x.id === intake.industry);
    if (i && intake.industry !== 'all') parts.push(i.label);
  }
  if (intake.size) {
    const s = SIZES.find((x) => x.id === intake.size);
    if (s) parts.push(s.label.replace(/\s*\(.*\)\s*$/, ''));
  }
  return parts.join(', ');
}

/**
 * Sandbox histogram — `<svg>` painted by `story-render.js`'s
 * renderHistogram on a ref-owned element. Phase F of the
 * imperative-renderer retirement
 * (docs/eng/pilot-engine-imperative-retirement.md): the SVG path
 * generation + axis ticks + label collision handling stay imperative
 * (~200 LOC; matches the canvas-paint exception in the architectural
 * invariant — "React owns the DOM, lib/pilot/ owns canvas-pixel +
 * SVG paint"). The bridge moves from main.js's runAndRender to this
 * component: a useEffect subscribes to sim worker results and re-
 * paints when they change.
 */
export function SandboxHistogram() {
  const svgRef = useRef<SVGSVGElement>(null);
  const {results} = useSimWorker();

  useEffect(() => {
    if (!results || !svgRef.current) return;
    // renderHistogram queries the SVG element by id; assign a stable
    // id so the existing imperative code path finds it. The id +
    // story-render.js's `opts.svgId` default ("svg-histo") match the
    // legacy callsite.
    svgRef.current.id = 'svg-histo';
    void renderHistogram(results.human, results.ai, {svgId: 'svg-histo'});
  }, [results]);

  return (
    <svg
      ref={svgRef}
      id="svg-histo"
      viewBox="0 0 800 220"
      preserveAspectRatio="none"
      className="w-full"
    />
  );
}

/**
 * Sandbox distribution headline + failure-rate bar — React replacement
 * for story-render.js's `renderHistogramCallout` (R6). Phase E of the
 * imperative-renderer retirement (docs/eng/pilot-engine-imperative-
 * retirement.md). Subscribes to useSimWorker `results.winProb` +
 * `results.human.failRate` + `results.ai.failRate`.
 */
export function SandboxDistHeadline() {
  const {results} = useSimWorker();
  const winProb = results?.winProb ?? 0;
  const exact = winProb >= 0.99995;
  const pct = exact ? '100' : (Math.floor(winProb * 1000) / 10).toFixed(1);
  return (
    <Subheading
      className="nb-dist-headline text-3xl/10 sm:text-4xl/12"
      id="nb-dist-headline"
    >
      {results
        ? `In ${pct}% of these 10,000 runs, Planapus finishes faster.`
        : 'Not a one-off. The gap holds across the distribution.'}
    </Subheading>
  );
}

export function SandboxFailbar() {
  const {results} = useSimWorker();
  const human = results?.human;
  const ai = results?.ai;
  if (!human || !ai) {
    return (
      <div
        id="nb-failrate-bar"
        className="rounded-2xl border border-taupe-200 bg-taupe-50 p-5 text-sm/6 text-taupe-700 dark:border-taupe-800 dark:bg-taupe-900 dark:text-taupe-400"
      >
        <Skel /> loading failure rates…
      </div>
    );
  }
  const max = Math.max(human.failRate, ai.failRate, 0.01);
  const hPct = (human.failRate / max) * 100;
  const aPct = (ai.failRate / max) * 100;
  return (
    <div
      id="nb-failrate-bar"
      className="flex flex-col gap-3 rounded-2xl border border-taupe-200 bg-taupe-50 p-5 text-sm/6 text-taupe-700 dark:border-taupe-800 dark:bg-taupe-900 dark:text-taupe-400"
    >
      <div className="font-mono text-xs/5 uppercase tracking-widest text-taupe-500">
        Failure rate (process dies mid-run)
      </div>
      <FailbarRow
        label="Today"
        pct={hPct}
        valuePct={human.failRate * 100}
        fillClass="bg-taupe-400 dark:bg-taupe-600"
      />
      <FailbarRow
        label="Planapus"
        pct={aPct}
        valuePct={ai.failRate * 100}
        fillClass="bg-taupe-950 dark:bg-taupe-100"
      />
    </div>
  );
}

function FailbarRow({
  label,
  pct,
  valuePct,
  fillClass,
}: {
  label: string;
  pct: number;
  valuePct: number;
  fillClass: string;
}) {
  return (
    <div className="flex items-center gap-3">
      <span className="w-20 shrink-0 text-xs/5 font-medium uppercase tracking-widest text-taupe-500">
        {label}
      </span>
      <div className="relative h-3 flex-1 overflow-hidden rounded-full bg-taupe-100 dark:bg-taupe-900">
        <div
          className={`h-full rounded-full ${fillClass}`}
          style={{width: `${pct.toFixed(1)}%`}}
        />
      </div>
      <span className="w-16 shrink-0 text-right text-sm/6 font-semibold tabular-nums text-taupe-950 dark:text-white">
        {valuePct.toFixed(1)}%
      </span>
    </div>
  );
}

/**
 * Sandbox per-step breakdown — two horizontal stacked bars (Today vs
 * Planapus), one segment per process step, plus a "biggest cuts" chip
 * row below. React replacement for story-render.js's
 * `renderStepBreakdown` (R5). Phase D of the imperative-renderer
 * retirement (docs/eng/pilot-engine-imperative-retirement.md).
 *
 * Subscribes to useSimWorker `results.breakdown.{human,ai}` for the
 * per-step `avgHrs` data; renders inline-Tailwind with taupe palette
 * (was: imperative innerHTML with `.nb-steps-*` classes painted by
 * the engine). The legacy CSS rules get retired alongside this
 * component.
 */
function fmtStepHrs(h: number): string {
  if (h < 1) return `${(h * 60).toFixed(0)}m`;
  if (h < 24) return `${h.toFixed(1)}h`;
  return `${(h / 24).toFixed(1)}d`;
}

function shortStepLabel(s: string): string {
  return s.length > 22 ? s.slice(0, 21) + '…' : s;
}

export function SandboxStepBreakdown() {
  const {results} = useSimWorker();
  const humanBreak = results?.breakdown?.human;
  const aiBreak = results?.breakdown?.ai;

  if (!humanBreak || !aiBreak || humanBreak.length === 0) {
    return (
      <div id="nb-steps" className="nb-steps mt-2 flex flex-col gap-3">
        <div className="text-sm/6 text-taupe-600 dark:text-taupe-500">
          <Skel /> per-step breakdown loading…
        </div>
      </div>
    );
  }

  const humanTotal = humanBreak.reduce((s, e) => s + e.avgHrs, 0);
  const aiTotal = aiBreak.reduce((s, e) => s + e.avgHrs, 0);
  const max = Math.max(humanTotal, aiTotal, 0.01);

  const cuts = humanBreak
    .map((h, i) => ({
      label: h.label,
      cutHrs: Math.max(0, h.avgHrs - (aiBreak[i]?.avgHrs ?? 0)),
    }))
    .sort((a, b) => b.cutHrs - a.cutHrs)
    .slice(0, 3)
    .filter((c) => c.cutHrs > 0.2);

  return (
    <div id="nb-steps" className="nb-steps mt-2 flex flex-col gap-4">
      <StepLane
        label="Today"
        labelClass="text-taupe-400"
        segClass="bg-taupe-400 dark:bg-taupe-600"
        steps={humanBreak}
        total={humanTotal}
        max={max}
      />
      <StepLane
        label="Planapus"
        labelClass="text-taupe-950 dark:text-taupe-100"
        segClass="bg-taupe-950 dark:bg-taupe-100"
        steps={aiBreak}
        total={aiTotal}
        max={max}
      />
      {/* Per the May 2026 functional/content review: step labels were
          truncating/disappearing inside the bar segments. Move them to
          a numbered list below so every step name is readable, with the
          Today/Planapus durations side by side. The bar above is the
          visual "shape of the cut"; the list below is the audit trail. */}
      <ol className="nb-steps-list grid grid-cols-1 gap-1 text-xs/5 text-taupe-700 dark:text-taupe-400 sm:grid-cols-2">
        {humanBreak.map((h, i) => {
          const a = aiBreak[i];
          return (
            <li
              key={`${i}-${h.label}`}
              className="flex items-baseline justify-between gap-3 rounded-md px-2 py-1 odd:bg-taupe-50 dark:odd:bg-taupe-900/50"
            >
              <span className="flex min-w-0 items-baseline gap-2">
                <span className="font-mono text-[10px] uppercase tracking-widest text-taupe-500">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span className="truncate text-taupe-950 dark:text-white" title={h.label}>
                  {h.label}
                </span>
              </span>
              <span className="shrink-0 tabular-nums">
                <span className="text-taupe-500">{fmtStepHrs(h.avgHrs)}</span>
                <span className="px-1 text-taupe-400">→</span>
                <span className="font-semibold text-taupe-950 dark:text-taupe-100">
                  {a ? fmtStepHrs(a.avgHrs) : '—'}
                </span>
              </span>
            </li>
          );
        })}
      </ol>
      {cuts.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-xs/5 text-taupe-700 dark:text-taupe-400">
          <span className="font-mono uppercase tracking-widest text-taupe-500">
            Biggest cuts:
          </span>
          {cuts.map((c) => (
            <span
              key={c.label}
              className="inline-flex items-center gap-1 rounded-full border border-taupe-200 bg-taupe-50 px-2 py-0.5 dark:border-taupe-700 dark:bg-taupe-800"
            >
              {c.label}{' '}
              <b className="text-taupe-950 dark:text-white">
                −{fmtStepHrs(c.cutHrs)}
              </b>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function StepLane({
  label,
  labelClass,
  segClass,
  steps,
  total,
  max,
}: {
  label: string;
  labelClass: string;
  segClass: string;
  steps: ReadonlyArray<{label: string; avgHrs: number}>;
  total: number;
  max: number;
}) {
  return (
    <div className="flex items-center gap-3">
      <div className={`w-20 shrink-0 text-sm/6 font-medium ${labelClass}`}>
        {label}
      </div>
      <div
        className="relative flex h-7 overflow-hidden rounded-full bg-taupe-100 dark:bg-taupe-900"
        style={{width: `${((total / max) * 100).toFixed(2)}%`, minWidth: 60}}
      >
        {steps.map((s, i) => {
          const pct = (s.avgHrs / max) * 100;
          if (pct < 0.4) return null;
          // Alternating opacity gives the bar a visible "shape of the
          // run" even without per-segment labels. Step names live in
          // the ordered list below (the May 2026 review called the
          // in-bar labels unreadable when segments were narrow).
          const opacity = i % 2 === 0 ? 0.95 : 0.65;
          const tip = `${i + 1}. ${s.label}: ${fmtStepHrs(s.avgHrs)}`;
          return (
            <div
              key={i}
              title={tip}
              className={`h-full ${segClass}`}
              style={{width: `${pct.toFixed(2)}%`, opacity}}
            />
          );
        })}
      </div>
      <div className="text-sm/6 tabular-nums text-taupe-700 dark:text-taupe-400">
        {fmtStepHrs(total)}
      </div>
    </div>
  );
}

/**
 * Sandbox "The savings" headline trio — `#nb-win-dollars` / `-hours`
 * / `-deals` + the dynamic deals label + the FTE-equivalent caption
 * under hours.
 */
export function SandboxHeroWins({proc}: SandboxPanelsProps) {
  const {knobs} = usePilot();
  const {results} = useSimWorker();

  const human = results?.human;
  const ai = results?.ai;
  const hourlyRate = knobs.hourlyRate;
  const noun = outcomeNounFor(proc);

  const hoursSavedPerProc = Math.max(0, (human?.avgHumanH ?? 0) - (ai?.avgHumanH ?? 0));
  const hoursPerYear = hoursSavedPerProc * PROCS_PER_YEAR;
  const dollarsPerYear = hoursPerYear * hourlyRate;
  const recovered = Math.max(0, (human?.failRate ?? 0) - (ai?.failRate ?? 0)) * PROCS_PER_YEAR;

  const dollarsRef = useRef<HTMLDivElement>(null);
  const hoursRef = useRef<HTMLDivElement>(null);
  const dealsRef = useRef<HTMLDivElement>(null);

  const dollarsTxt = useCountUp(dollarsPerYear, fmtUSD, dollarsRef);
  const hoursTxt = useCountUp(hoursPerYear, fmtHours, hoursRef);
  const dealsTxt = useCountUp(recovered, fmtDeals, dealsRef);

  return (
    <div
      className="nb-wins grid grid-cols-1 gap-8 sm:grid-cols-3"
      id="nb-wins"
    >
      <div className="nb-win flex flex-col gap-1">
        <div
          ref={dollarsRef}
          id="nb-win-dollars"
          className="nb-win-num font-display text-4xl/none tracking-tight text-taupe-950 sm:text-5xl/none dark:text-white"
        >
          {results ? dollarsTxt : <Skel />}
        </div>
        <div className="nb-win-label text-sm/6 text-taupe-700 dark:text-taupe-400">
          coordination cost saved
          <Tt text="Labor hours reclaimed per process × runs per year × loaded hourly cost." />
        </div>
      </div>
      <div className="nb-win flex flex-col gap-1">
        <div
          ref={hoursRef}
          id="nb-win-hours"
          className="nb-win-num font-display text-4xl/none tracking-tight text-taupe-950 sm:text-5xl/none dark:text-white"
        >
          {results ? hoursTxt : <Skel />}
        </div>
        <div className="nb-win-label text-sm/6 text-taupe-700 dark:text-taupe-400">
          hours reclaimed
          <Tt text="Hands-on labor that today disappears into status chasing, email tag, and rework." />
        </div>
        <span
          id="nb-win-hours-caption"
          className="nb-win-caption text-xs/5 text-taupe-600 dark:text-taupe-500"
        >
          {hoursPerYear > 0 ? fteEquivalent(hoursPerYear) : ''}
        </span>
      </div>
      <div className="nb-win flex flex-col gap-1">
        <div
          ref={dealsRef}
          id="nb-win-deals"
          className="nb-win-num font-display text-4xl/none tracking-tight text-taupe-950 sm:text-5xl/none dark:text-white"
        >
          {results ? dealsTxt : <Skel />}
        </div>
        <div
          id="nb-win-deals-label"
          className="nb-win-label text-sm/6 text-taupe-700 dark:text-taupe-400"
        >
          {noun} saved
          <Tt
            text={`Difference in the process-failure rate (today vs. Planapus) × runs per year. These are ${noun} that today die mid-process.`}
          />
        </div>
        <p
          id="nb-win-why"
          className="nb-win-why text-xs/5 text-taupe-600 dark:text-taupe-500"
        >
          Planapus catches dropped handoffs that today die at OOO walls or
          5-day silences.
        </p>
      </div>
    </div>
  );
}

/**
 * SandboxPortfolioWins — Phase E (rest) of the imperative-renderer
 * retirement. Replaces story-render.js#renderPortfolioWins (R5b). Picks
 * a portfolio of up to PORTFOLIO_TARGET processes that match the
 * caller's intake (industry/size), runs a synchronous Monte Carlo
 * summary per process at PORTFOLIO_TRIALS each, and sums the savings.
 *
 * The full 10k-trial worker run still drives <SandboxHistogram> /
 * <SandboxHeroWins> / etc. for the *current* process; this rollup
 * answers "across an org like yours, what would Planapus reclaim?" and
 * intentionally uses a smaller trial count + sync main-thread compute
 * since the workload is shorter (~10 procs × 2 modes × 1500 trials).
 */
export function SandboxPortfolioWins({proc}: SandboxPanelsProps) {
  const {knobs, intake} = usePilot();

  const picks = useMemo(
    () => (proc ? pickPortfolio(PROCESSES, proc, intake, PORTFOLIO_TARGET) : []),
    [proc, intake],
  );

  // Per-knob recompute. runMonteCarloSummary reads intake from the
  // bridge (localStorage) so we don't need to thread intake into the
  // deps separately — but we DO depend on `picks` (intake-derived) so
  // the deps are sufficient.
  const result = useMemo(() => {
    if (!picks.length) return null;
    let totalHours = 0;
    let totalDeals = 0;
    const breakdown: {
      proc: ProcessDef;
      hoursPerYear: number;
      dealsRecov: number;
    }[] = [];
    // SimParams is a fixed-key interface; widen to Record<string,number>
    // for the lower-level summary call (which uses index access).
    const params = knobs as unknown as Record<string, number>;
    for (const p of picks) {
      const humanSum = runMonteCarloSummary(p, params, PORTFOLIO_TRIALS, false);
      const aiSum = runMonteCarloSummary(p, params, PORTFOLIO_TRIALS, true);
      const hoursSavedPerProc = Math.max(0, humanSum.avgHumanH - aiSum.avgHumanH);
      const hoursPerYear = hoursSavedPerProc * PROCS_PER_YEAR;
      const dealsRecov =
        Math.max(0, humanSum.failRate - aiSum.failRate) * PROCS_PER_YEAR;
      totalHours += hoursPerYear;
      totalDeals += dealsRecov;
      breakdown.push({proc: p, hoursPerYear, dealsRecov});
    }
    return {
      totalHours,
      totalDeals,
      totalDollars: totalHours * knobs.hourlyRate,
      breakdown,
    };
  }, [picks, knobs]);

  const dollarsRef = useRef<HTMLDivElement>(null);
  const hoursRef = useRef<HTMLDivElement>(null);
  const dealsRef = useRef<HTMLDivElement>(null);
  const dollarsTxt = useCountUp(result?.totalDollars ?? 0, fmtUSD, dollarsRef);
  const hoursTxt = useCountUp(result?.totalHours ?? 0, fmtScaleHours, hoursRef);
  const dealsTxt = useCountUp(result?.totalDeals ?? 0, fmtScaleDeals, dealsRef);

  const contextLabel = contextLabelFromIntake(intake);
  const promptSuffix = contextLabel ? ` in a ${contextLabel} org` : '';

  return (
    <div className="nb-scale-block flex flex-col gap-6 rounded-2xl border border-taupe-200 bg-taupe-50 p-6 dark:border-taupe-800 dark:bg-taupe-900">
      <Text id="nb-scale-prompt" className="nb-scale-prompt">
        {result ? (
          <>
            Across <b>{picks.length}</b> typical multi-party processes
            {promptSuffix}, the same drag costs:
          </>
        ) : (
          <>
            Across the operations that drag in an org like yours, Planapus
            would reclaim:
          </>
        )}
      </Text>
      <div className="nb-scale-wins grid grid-cols-1 gap-8 sm:grid-cols-3">
        <div className="nb-scale-win flex flex-col gap-1">
          <div
            ref={dollarsRef}
            id="nb-scale-dollars"
            className="nb-scale-num font-display text-4xl/none tracking-tight text-taupe-950 sm:text-5xl/none dark:text-white"
          >
            {result ? dollarsTxt : <Skel />}
          </div>
          <div className="nb-scale-label text-sm/6 text-taupe-700 dark:text-taupe-400">
            coordination cost saved
          </div>
        </div>
        <div className="nb-scale-win flex flex-col gap-1">
          <div
            ref={hoursRef}
            id="nb-scale-hours"
            className="nb-scale-num font-display text-4xl/none tracking-tight text-taupe-950 sm:text-5xl/none dark:text-white"
          >
            {result ? hoursTxt : <Skel />}
          </div>
          <div className="nb-scale-label text-sm/6 text-taupe-700 dark:text-taupe-400">
            hours reclaimed
          </div>
          <div
            id="nb-scale-hours-caption"
            className="nb-win-caption text-xs/5 text-taupe-600 dark:text-taupe-500"
          >
            {result && result.totalHours > 0 ? fteEquivalent(result.totalHours) : ''}
          </div>
        </div>
        <div className="nb-scale-win flex flex-col gap-1">
          <div
            ref={dealsRef}
            id="nb-scale-deals"
            className="nb-scale-num font-display text-4xl/none tracking-tight text-taupe-950 sm:text-5xl/none dark:text-white"
          >
            {result ? dealsTxt : <Skel />}
          </div>
          <div className="nb-scale-label text-sm/6 text-taupe-700 dark:text-taupe-400">
            runs saved
          </div>
        </div>
      </div>
      <div
        id="nb-scale-list"
        className="nb-scale-list flex flex-wrap gap-2"
      >
        {result?.breakdown.map((b) => (
          <span
            key={b.proc.id}
            title={`≈ ${fmtScaleHours(b.hoursPerYear)} reclaimed / yr by Planapus`}
            className="inline-flex items-center gap-2 rounded-full border border-taupe-200 bg-white px-3 py-1 text-xs/5 text-taupe-700 dark:border-taupe-700 dark:bg-taupe-950 dark:text-taupe-300"
          >
            <span
              aria-hidden
              className="inline-flex size-3.5 items-center justify-center text-taupe-600 dark:text-taupe-400"
              dangerouslySetInnerHTML={{__html: categoryGlyphSvg(b.proc.category || '', 14)}}
            />
            <span className="font-medium text-taupe-950 dark:text-white">{b.proc.name}</span>
            <span className="text-taupe-600 dark:text-taupe-500">{fmtScaleHours(b.hoursPerYear)}</span>
          </span>
        ))}
      </div>
      <Text
        id="nb-scale-note"
        className="nb-scale-note text-xs/5 text-taupe-600 dark:text-taupe-500"
      >
        {result
          ? `Each process simulated independently with your dial settings × ${PROCS_PER_YEAR} runs/year. The totals are the sum of per-process savings, not an extrapolation.`
          : `Each process is simulated independently with your current dial settings. The totals above are the sum of per-process savings, not an extrapolation.`}
      </Text>
    </div>
  );
}

/**
 * Sandbox "Today's coordination tax" headline trio — `#nb-tax-dollars`
 * / `-hours` / `-deals` + the dynamic deals label + the FTE-equivalent
 * caption.
 */
export function SandboxTodaysTax({proc}: SandboxPanelsProps) {
  const {knobs} = usePilot();
  const {results} = useSimWorker();

  const human = results?.human;
  const hourlyRate = knobs.hourlyRate;
  const noun = outcomeNounFor(proc);

  const humanH = (human?.avgHumanH ?? 0) * PROCS_PER_YEAR;
  const dollars = humanH * hourlyRate;
  const failed = (human?.failRate ?? 0) * PROCS_PER_YEAR;

  const dollarsRef = useRef<HTMLDivElement>(null);
  const hoursRef = useRef<HTMLDivElement>(null);
  const dealsRef = useRef<HTMLDivElement>(null);

  const dollarsTxt = useCountUp(dollars, fmtUSD, dollarsRef);
  const hoursTxt = useCountUp(humanH, fmtHours, hoursRef);
  const dealsTxt = useCountUp(failed, fmtDeals, dealsRef);

  return (
    <>
      <div className="nb-tax-wins grid grid-cols-1 gap-8 sm:grid-cols-3">
        <div className="nb-tax-win flex flex-col gap-1">
          <div
            ref={dollarsRef}
            id="nb-tax-dollars"
            className="nb-tax-num font-display text-4xl/none tracking-tight text-taupe-950 sm:text-5xl/none dark:text-white"
          >
            {results ? dollarsTxt : <Skel />}
          </div>
          <div className="nb-tax-label text-sm/6 text-taupe-700 dark:text-taupe-400">
            per year, in labor
          </div>
        </div>
        <div className="nb-tax-win flex flex-col gap-1">
          <div
            ref={hoursRef}
            id="nb-tax-hours"
            className="nb-tax-num font-display text-4xl/none tracking-tight text-taupe-950 sm:text-5xl/none dark:text-white"
          >
            {results ? hoursTxt : <Skel />}
          </div>
          <div className="nb-tax-label text-sm/6 text-taupe-700 dark:text-taupe-400">
            stuck in handoffs
          </div>
        </div>
        <div className="nb-tax-win flex flex-col gap-1">
          <div
            ref={dealsRef}
            id="nb-tax-deals"
            className="nb-tax-num font-display text-4xl/none tracking-tight text-taupe-950 sm:text-5xl/none dark:text-white"
          >
            {results ? dealsTxt : <Skel />}
          </div>
          <div
            id="nb-tax-deals-label"
            className="nb-tax-label text-sm/6 text-taupe-700 dark:text-taupe-400"
          >
            {noun} lost / yr
          </div>
        </div>
      </div>
      <p
        id="nb-tax-caption"
        className="nb-tax-caption text-sm/6 text-taupe-700 dark:text-taupe-400"
      >
        {humanH > 0
          ? `That's what the status quo is costing: ${fteEquivalent(humanH)} burned on coordination alone.`
          : ''}
      </p>
    </>
  );
}

/**
 * Sandbox `#nb-intro` header — process name (with inline category
 * glyph) + description. React replacement for story-render.js's
 * `renderProcessMeta` (R2). Phase B step 3 of the imperative-renderer
 * retirement (docs/eng/pilot-engine-imperative-retirement.md).
 *
 * Renders a `<Skel />` placeholder before a process is picked; once
 * `proc` is non-null, the engine never overwrites the header again
 * because main.js stopped calling renderProcessMeta in the same
 * commit.
 */
export function SandboxIntroHeader({proc}: SandboxPanelsProps) {
  if (!proc) {
    return (
      <h2
        id="nb-proc-name"
        className="nb-hr-title font-display text-4xl/none tracking-tight text-taupe-950 sm:text-5xl/none dark:text-white"
      >
        <Skel />
      </h2>
    );
  }
  return (
    <>
      <h2
        id="nb-proc-name"
        className="nb-hr-title font-display text-4xl/none tracking-tight text-taupe-950 sm:text-5xl/none dark:text-white"
      >
        <span
          className="mr-2 inline-flex align-[-0.15em]"
          aria-hidden="true"
          dangerouslySetInnerHTML={{__html: categoryGlyphSvg(proc.category || '', 20)}}
        />
        <span className="inline-flex items-center">{proc.name}</span>
      </h2>
      <p
        id="nb-proc-desc"
        className="nb-hr-desc text-lg/8 text-taupe-700 dark:text-taupe-400"
      >
        {proc.short || proc.desc}
      </p>
    </>
  );
}

/** SandboxFab — floating "↻ Try another process" button. */
export function SandboxFloatingFab({
  hasProcess,
  onClick,
}: {
  hasProcess: boolean;
  onClick: () => void;
}) {
  return <SandboxFab onClick={onClick} hasProcess={hasProcess} />;
}

/**
 * SandboxJumpOrb — floating "jump to results" pill. Phase G of the
 * imperative-renderer retirement (R6) replaces main.js's wireJumpOrb +
 * setJumpOrb* + updateJumpOrb chain. Visibility tracks the #nb-knobs
 * section via IntersectionObserver. While the worker is running, the
 * pill shows "<done> / 10,000 runs" + a progress bar; once a run
 * finishes while the user is still in the knobs section, the label
 * flips to "Fresh results ready ↑" so the next click jumps back up.
 */
function SandboxJumpOrb() {
  const {status, progress} = useSimWorker();
  const [inKnobs, setInKnobs] = useState(false);
  const [freshArmed, setFreshArmed] = useState(false);
  const wasLoadingRef = useRef(false);
  const loading = status === 'running';

  useEffect(() => {
    if (wasLoadingRef.current && !loading && inKnobs) {
      setFreshArmed(true);
    }
    if (loading) setFreshArmed(false);
    wasLoadingRef.current = loading;
  }, [loading, inKnobs]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const knobs = document.querySelector('#nb-knobs');
    if (!knobs) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) setInKnobs(e.intersectionRatio > 0.1);
      },
      {threshold: [0, 0.1, 0.5]},
    );
    observer.observe(knobs);
    return () => observer.disconnect();
  }, []);

  const fraction = progress?.fraction ?? 0;
  const done = progress?.done ?? 0;

  function handleClick() {
    const target =
      document.querySelector('#nb-impact') ?? document.querySelector('#nb-race');
    if (target instanceof HTMLElement) {
      target.scrollIntoView({behavior: 'smooth', block: 'start'});
    }
    setFreshArmed(false);
  }

  return (
    <button
      type="button"
      id="nb-jump-orb"
      className="nb-jump-orb fixed bottom-6 left-1/2 z-30 -translate-x-1/2 items-center gap-3 rounded-full border border-taupe-300 bg-white px-5 py-2 text-sm/6 font-medium text-taupe-950 shadow-lg dark:border-taupe-700 dark:bg-taupe-900 dark:text-white [&:not([hidden])]:inline-flex"
      hidden={!inKnobs}
      aria-live="polite"
      onClick={handleClick}
    >
      {loading ? (
        <>
          <span className="nb-jump-orb-running inline-flex items-center gap-1">
            <span className="nb-jump-orb-count tabular-nums">
              {(done | 0).toLocaleString()}
            </span>
            <span className="nb-jump-orb-of text-taupe-600 dark:text-taupe-500">
              / 10,000 runs
            </span>
          </span>
          <span className="nb-jump-orb-bar-wrap inline-block h-1 w-24 overflow-hidden rounded-full bg-taupe-200 dark:bg-taupe-800">
            <span
              className="nb-jump-orb-bar-fill block h-full bg-taupe-950 dark:bg-taupe-100"
              style={{width: `${Math.round(fraction * 100)}%`}}
            />
          </span>
        </>
      ) : (
        <span className="nb-jump-orb-label">
          {freshArmed ? 'Fresh results ready ↓' : 'See your results ↓'}
        </span>
      )}
    </button>
  );
}

/**
 * SandboxTldrStrip — sticky one-line summary that appears once the
 * visitor has scrolled past the per-process wins block. Phase G of
 * the imperative-renderer retirement (R6) replaces main.js's
 * wireTldrVisibility + updateTldrStrip pair. Visibility is governed
 * by an IntersectionObserver on #nb-wins, numbers come from the sim
 * worker results + live knobs.
 */
function SandboxTldrStrip({proc, onTalk}: {proc: ProcessDef | null; onTalk: () => void}) {
  const {knobs} = usePilot();
  const {results} = useSimWorker();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const wins = document.querySelector('#nb-wins');
    if (!wins) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          const show = !e.isIntersecting && e.boundingClientRect.top < 0;
          setVisible(show);
        }
      },
      {threshold: 0},
    );
    observer.observe(wins);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.body.classList.toggle('tldr-pinned', visible);
    return () => {
      document.body.classList.remove('tldr-pinned');
    };
  }, [visible]);

  const human = results?.human;
  const ai = results?.ai;
  const hoursSavedPerProc = Math.max(0, (human?.avgHumanH ?? 0) - (ai?.avgHumanH ?? 0));
  const hoursPerYear = hoursSavedPerProc * PROCS_PER_YEAR;
  const dollarsPerYear = hoursPerYear * knobs.hourlyRate;
  const dealsSaved =
    Math.max(0, (human?.failRate ?? 0) - (ai?.failRate ?? 0)) * PROCS_PER_YEAR;

  return (
    <div
      className="nb-tldr-strip sticky top-20 z-20 items-center justify-between gap-4 rounded-full border border-taupe-200 bg-white/90 px-5 py-2 shadow-sm backdrop-blur [&:not([hidden])]:flex dark:border-taupe-800 dark:bg-taupe-900/90"
      id="nb-tldr-strip"
      hidden={!visible}
    >
      <div className="nb-tldr-content flex flex-wrap items-center gap-3 text-sm/6 text-taupe-700 dark:text-taupe-400">
        <span
          className="nb-tldr-proc inline-flex items-center gap-1.5 font-medium text-taupe-950 dark:text-white"
          id="nb-tldr-proc"
        >
          {proc ? (
            <>
              <span
                aria-hidden
                className="inline-flex"
                dangerouslySetInnerHTML={{
                  __html: categoryGlyphSvg(proc.category || '', 16),
                }}
              />
              <span>{proc.name}</span>
            </>
          ) : (
            <Skel />
          )}
        </span>
        <span className="nb-tldr-sep text-taupe-400">·</span>
        <span className="nb-tldr-stat">
          <b id="nb-tldr-dollars" className="text-taupe-950 dark:text-white">
            {results ? fmtUSD(dollarsPerYear) : <Skel />}
          </b>{' '}
          saved / yr
        </span>
        <span className="nb-tldr-sep text-taupe-400">·</span>
        <span className="nb-tldr-stat">
          <b id="nb-tldr-hours" className="text-taupe-950 dark:text-white">
            {results ? `${Math.round(hoursPerYear).toLocaleString()} hrs` : <Skel />}
          </b>{' '}
          reclaimed
        </span>
        <span className="nb-tldr-sep text-taupe-400">·</span>
        <span className="nb-tldr-stat">
          <b id="nb-tldr-deals" className="text-taupe-950 dark:text-white">
            {results
              ? dealsSaved >= 10
                ? `${Math.round(dealsSaved)}`
                : dealsSaved.toFixed(1)
              : <Skel />}
          </b>{' '}
          saved from failure
        </span>
      </div>
      <Button className="btn-primary nb-tldr-cta" id="nb-tldr-cta" onClick={onTalk}>
        Book a walkthrough →
      </Button>
    </div>
  );
}

/**
 * SandboxAIActions — "What Planapus is doing on the right lane" list.
 * Phase G of the imperative-renderer retirement replaces main.js's
 * renderAIActionsList; the actions come from aiActionsFor(proc).
 */
function SandboxAIActions({proc}: {proc: ProcessDef | null}) {
  const actions = useMemo(() => aiActionsFor(proc), [proc]);
  return (
    <ul
      id="nb-ai-actions-list"
      className="nb-ai-actions-list flex flex-col gap-2 text-sm/6 text-taupe-700 dark:text-taupe-400"
    >
      {actions.map((a, i) => (
        <li key={i}>{a}</li>
      ))}
    </ul>
  );
}

/**
 * SandboxShareButton — "🔗 Share these numbers" CTA. Phase J of the
 * imperative-renderer retirement replaces main.js's copyShareableUrl +
 * shareBtn click listener. Builds the share URL from the current
 * process + intake + non-default knobs via buildSandboxShareUrl, then
 * copies to the clipboard with a "✓ Link copied" confirmation flash.
 */
function SandboxShareButton({proc}: {proc: ProcessDef | null}) {
  const {knobs, intake} = usePilot();
  const [flash, setFlash] = useState<string | null>(null);

  const handleClick = useCallback(async () => {
    const url = buildSandboxShareUrl({
      processId: proc?.id ?? null,
      intake,
      knobs: knobs as unknown as Partial<Record<ParamId, number>>,
    });
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Fallback for older browsers / non-secure contexts
      const ta = document.createElement('textarea');
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
      } catch {
        /* clipboard failed — silently degrade */
      }
      ta.remove();
    }
    setFlash('✓ Link copied');
    window.setTimeout(() => setFlash(null), 1800);
  }, [proc, intake, knobs]);

  return (
    <Button
      // `color="light"` is the canonical light-on-light secondary
      // variant. Per the May 2026 functional/content review the share
      // button rendered as taupe-950-on-taupe-950 (invisible) in light
      // mode because the className `bg-white` lost the Tailwind
      // category-order race to the Button's default `bg-taupe-950`.
      // Using the variant prop instead of overriding via className
      // routes the bg/text through the same path the rest of the
      // button system uses.
      color="light"
      size="lg"
      className="border border-taupe-300 dark:border-taupe-700"
      id="nb-share-cta"
      onClick={handleClick}
    >
      {flash ?? '🔗 Share these numbers'}
    </Button>
  );
}


/* ============================================================
   Sandbox view (default export) — composition of the building
   blocks above + the static notebook sections (TLDR strip, taxes,
   per-step breakdown, savings, distribution, knobs, talk CTA).
   Folded from the prior SandboxSections.tsx in Phase 3 of the
   Oatmeal rebuild.
   ============================================================ */

const SANDBOX_SECTIONS: ReadonlyArray<SceneDescriptor> = [
  {id: 'nb-intro', title: 'The use case'},
  {id: 'nb-knobs', title: 'Your numbers'},
  {id: 'nb-tax', title: "Today's tax"},
  {id: 'nb-race', title: 'Watch them race'},
  {id: 'nb-steps-section', title: 'Where the time goes'},
  {id: 'nb-impact', title: 'The savings'},
  {id: 'nb-dist', title: 'Across 10,000 runs'},
  {id: 'nb-talk-cta', title: 'Talk to us'},
  {id: 'nb-portfolio', title: 'Zoom out'},
];

/**
 * SandboxSceneDots — left-side dot navigator for the Sandbox view.
 *
 * Mirrors Story's <SceneDots>. Per the May 2026 functional/content
 * review, the prior dot navigator only worked in Story; Sandbox got an
 * orphan column of empty dots on the left edge. This component scopes
 * the spy + dot stack to the Sandbox sections so the same affordance
 * works on both views.
 */
function SandboxSceneDots() {
  const {viewMode} = usePilot();
  const sectionRefs = useRef<
    Record<string, React.MutableRefObject<HTMLElement | null>>
  >({});
  for (const s of SANDBOX_SECTIONS) {
    if (!sectionRefs.current[s.id]) {
      sectionRefs.current[s.id] = {current: null};
    }
  }
  useEffect(() => {
    for (const s of SANDBOX_SECTIONS) {
      const el = document.getElementById(s.id);
      if (el) sectionRefs.current[s.id].current = el;
    }
  });
  const spyTargets = useMemo(
    () => SANDBOX_SECTIONS.map((s) => ({id: s.id, ref: sectionRefs.current[s.id]})),
    [],
  );
  const {activeId, scrollTo, setActiveId} = useScrollSpy(spyTargets, {
    disabled: viewMode !== 'sandbox',
  });
  const onSelect = useCallback(
    (id: string) => {
      scrollTo(id);
      setActiveId(id);
    },
    [scrollTo, setActiveId],
  );
  // Story owns the dots in story mode (<SceneDots> in Story.tsx);
  // Sandbox owns them in sandbox mode. Mounting both at once stacks
  // two columns of dots on the left edge.
  if (viewMode !== 'sandbox') return null;
  return <SceneDots scenes={[...SANDBOX_SECTIONS]} activeId={activeId} onSelect={onSelect} />;
}

export default function Sandbox() {
  const {currentProcessId, selectProcess, viewMode} = usePilot();

  // Subscribe to the process catalog. Custom (Claude-generated)
  // processes get added to PROCESSES at runtime by claude.ts; React
  // doesn't have a subscription channel for that today, so we re-read
  // the catalog whenever `currentProcessId` changes.
  const proc: ProcessDef | null = useMemo(() => {
    if (!currentProcessId) return null;
    return PROCESSES[currentProcessId] || null;
  }, [currentProcessId]);

  // Mirror the legacy `loadProcess`'s effect of revealing the results
  // section. Pre-pick, main.js kept `#nb-results[hidden]` true; once a
  // process was picked, it removed the attribute. With React now
  // owning the picked-process state, we drive the same toggle.
  const showResults = !!proc;

  // Imperative → React bridge for process picks. `loadProcess` in
  // `lib/pilot/pilot-engine.js` (called by `modals.js`'s change-
  // process callback + the `mp:unlock` handler) dispatches
  // `pilot:process-selected` with the id so PilotContext can record
  // it. URL deep-links go through PilotContext's initializer
  // directly without this listener.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    function onPick(e: Event) {
      const detail = (e as CustomEvent<{processId?: string}>).detail;
      const id = detail?.processId;
      if (typeof id === 'string' && id) selectProcess(id);
    }
    window.addEventListener(PILOT_PROCESS_SELECTED_EVENT, onPick);
    return () => window.removeEventListener(PILOT_PROCESS_SELECTED_EVENT, onPick);
  }, [selectProcess]);

  // FAB click: switch to Story mode and scroll to the use-case picker
  // (Story scene 2). The prior catalog-in-modal flow was removed in the
  // May 2026 functional/content review — picking a different use case
  // now happens in one place, the Story picker.
  const openChangeModal = () => {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(
      new CustomEvent(PILOT_SET_VIEW_MODE_EVENT, {detail: 'story'}),
    );
    requestAnimationFrame(() => {
      const picker = document.getElementById('story-scene-2');
      if (picker) {
        picker.scrollIntoView({behavior: 'smooth', block: 'start'});
      }
    });
  };

  return (
    <>
      <section
        className="notebook"
        id="notebook"
        // F17 Slice D: was gated by `body.story-mode .notebook
        // { display: block }` + the inverse hide rules in
        // pitch/demo/vis modes. Now structurally hidden via
        // React state — keeps the in-flight sim worker store + the
        // visitor's scroll position across view switches.
        hidden={viewMode !== 'story' && viewMode !== 'sandbox'}
      >
        {/* Floating jump-to-results orb — Phase G of the imperative-
            renderer retirement. <SandboxJumpOrb> subscribes to
            useSimWorker progress + status and an IntersectionObserver
            on #nb-knobs. */}
        <SandboxJumpOrb />

        <article
          className="nb-results flex flex-col gap-24 py-16 sm:py-24 [&[hidden]]:hidden"
          id="nb-results"
          hidden={!showResults}
        >
          {/* Report gate — engine-managed (Claude-custom process flow) */}
          <div
            className="report-gate fixed inset-0 z-40 hidden items-center justify-center bg-taupe-950/45 p-6 backdrop-blur-sm [&:not([hidden])]:flex"
            id="report-gate"
            hidden
          >
            <div className="report-gate-card flex max-w-md flex-col gap-4 rounded-2xl border border-taupe-200 bg-white p-8 shadow-xl dark:border-taupe-800 dark:bg-taupe-950">
              <Eyebrow className="nb-eyebrow">Your custom report is ready</Eyebrow>
              <h2 className="font-display text-3xl/10 text-taupe-950 dark:text-white">
                Drop your work email to unlock the full simulation.
              </h2>
              <Text className="report-gate-sub">
                We've already simulated your process. The numbers are right
                there, just blurred. Tell us where to send the report and the
                simulation comes into focus.
              </Text>
              <div className="report-gate-form flex flex-col gap-3">
                <input
                  type="email"
                  id="report-gate-email"
                  placeholder="you@company.com"
                  autoComplete="email"
                  className="rounded-lg border border-taupe-300 bg-white px-3 py-2 text-sm text-taupe-950 placeholder:text-taupe-500 focus:border-taupe-950 focus:outline-none focus:ring-2 focus:ring-taupe-300 dark:border-taupe-700 dark:bg-taupe-900 dark:text-white"
                />
                <Button
                  className="btn-primary"
                  id="report-gate-submit"
                  size="lg"
                >
                  Unlock my report →
                </Button>
              </div>
              <p
                className="report-gate-error min-h-[1.25rem] text-sm/5 text-red-700"
                id="report-gate-error"
              />
            </div>
          </div>

          <hr className="nb-rule border-taupe-200 dark:border-taupe-800" />

          {/* Sticky TL;DR strip — Phase G of the imperative-renderer
              retirement. <SandboxTldrStrip> subscribes to useSimWorker
              results + intake/knobs and an IntersectionObserver on
              #nb-wins; it also toggles document.body's
              `.tldr-pinned` class. */}
          <SandboxTldrStrip
            proc={proc}
            onTalk={() => {
              if (typeof window === 'undefined') return;
              window.dispatchEvent(new CustomEvent(PILOT_OPEN_TALK_MODAL_EVENT));
            }}
          />

          <Container className="flex flex-col gap-24">
            {/* Intro — name/desc/meta are React-driven from `proc`. */}
            <section className="nb-section nb-headline flex flex-col gap-3" id="nb-intro">
              <SandboxIntroHeader proc={proc} />
              <SandboxStatRow proc={proc} />
            </section>

            {/* Knobs — moved to position #2 (just below the use case
                header) per the May 2026 functional/content review.
                Reasoning: the visitor lands in Sandbox to dial their own
                numbers — putting the dials at the bottom buried the
                primary affordance. With knobs here, every downstream
                number (today's tax, race, savings, distribution) reads
                as "your numbers" instead of "our defaults". */}
            <section className="nb-section nb-knobs flex flex-col gap-6" id="nb-knobs">
              <Eyebrow className="nb-eyebrow">Your numbers</Eyebrow>
              <Subheading id="nb-knobs-title" className="text-3xl/10 sm:text-4xl/12">
                Defaults are conservative industry averages. Dial them to
                match your team.
              </Subheading>
              <Text className="nb-section-sub">
                Hourly rate is the single biggest lever on the dollar figure.
                Change it first. Everything below recomputes as you move
                the sliders.
              </Text>
              <SandboxKnobGrid proc={proc} />
            </section>

            {/* Today's tax — React-owned via <SandboxTodaysTax>. */}
            <section className="nb-section nb-tax flex flex-col gap-6" id="nb-tax">
              <Eyebrow className="nb-eyebrow">Today's coordination tax</Eyebrow>
              <Text size="lg" className="nb-tax-prompt">
                Running this process the human-only way is costing you
                <Tt text="Each value is the human-routed cost: avg human hours per run × runs/year × loaded hourly cost. Failure count is process-fail-rate × runs/year." />
              </Text>
              <SandboxTodaysTax proc={proc} />
            </section>

            {/* Race — React-owned (Phase 5). */}
            <section className="nb-section nb-race flex flex-col gap-8" id="nb-race">
              <div className="nb-race-head flex flex-wrap items-end justify-between gap-4">
                <div className="flex flex-col gap-2">
                  <Eyebrow className="nb-eyebrow">Watch them race</Eyebrow>
                  <Subheading className="text-3xl/10 sm:text-4xl/12">
                    Same work. Same people. Different routing.
                  </Subheading>
                  <Text className="nb-race-sub">
                    <span className="nb-race-tip">
                      Click either lane to skip / replay.
                    </span>
                  </Text>
                </div>
              </div>

              <SandboxRaceLanes proc={proc} />

              {/* What Planapus does on the AI lane. */}
              <div
                className="nb-ai-actions flex flex-col gap-3 rounded-2xl border border-taupe-200 bg-taupe-50 p-5 dark:border-taupe-800 dark:bg-taupe-900"
                id="nb-ai-actions"
              >
                <p className="nb-ai-actions-eyebrow text-xs/5 font-semibold uppercase tracking-widest text-taupe-600 dark:text-taupe-400">
                  What Planapus is doing on the right lane
                </p>
                <SandboxAIActions proc={proc} />
              </div>
            </section>

            {/* Per-step breakdown — engine paints. */}
            <section className="nb-section nb-steps-section flex flex-col gap-4" id="nb-steps-section">
              <Eyebrow className="nb-eyebrow">Where the time goes</Eyebrow>
              <Subheading className="text-3xl/10 sm:text-4xl/12">
                Each handoff, today vs. Planapus.
              </Subheading>
              <Text className="nb-section-sub">
                The biggest cuts are the silent waits, when a handoff sits in
                someone's inbox over a weekend.
              </Text>
              <SandboxStepBreakdown />
            </section>

            {/* Savings — React-owned via <SandboxHeroWins>. Portfolio
                rollup was previously nested here; the May 2026 review
                called it out as confusing (Story+Sandbox are tied to
                the picked use case, then this showed 10 use cases). The
                rollup now lives in its own dedicated section at the
                bottom of the page, clearly separated from the
                per-process savings. */}
            <section className="nb-section nb-impact flex flex-col gap-8" id="nb-impact">
              <Eyebrow className="nb-eyebrow">The savings</Eyebrow>
              <Text size="lg" className="nb-wins-prompt">
                Planapus reclaims, per year
                <Tt text="Delta vs. the human-only baseline above. Drawn from 10,000 Monte Carlo simulations using the knob values you set above." />
              </Text>
              <SandboxHeroWins proc={proc} />

              <div className="nb-impact-cta flex flex-col items-start gap-2">
                <div className="nb-impact-actions flex flex-wrap gap-3">
                  <Button className="btn-primary" id="nb-email-cta" size="lg">
                    Email me this report →
                  </Button>
                  <SandboxShareButton proc={proc} />
                </div>
                <p className="nb-impact-cta-note text-xs/5 text-taupe-600 dark:text-taupe-500">
                  Full breakdown: numbers, assumptions, and a one-page action
                  plan.
                </p>
              </div>
            </section>

            {/* Distribution — Sandbox keeps the headline + failure-rate
                bar, but the histogram itself was removed in the May 2026
                content review (Parsa: "duplicate of the one in Story,
                let's consolidate on one"). The full distribution shape
                stays in Story scene 6; here we surface the failure
                deltas the visitor's knob settings produce. */}
            <section className="nb-section nb-dist flex flex-col gap-6">
              <Eyebrow className="nb-eyebrow">Across 10,000 runs</Eyebrow>
              <SandboxDistHeadline />
              <noscript>
                <Subheading className="nb-dist-headline text-3xl/10 sm:text-4xl/12">
                  Not a one-off. The gap holds across the distribution.
                </Subheading>
              </noscript>
              <SandboxFailbar />
            </section>

            {/* Closing CTA */}
            <section className="nb-section nb-talk-cta flex flex-col items-start gap-4">
              <Eyebrow className="nb-eyebrow">Your operations, your numbers</Eyebrow>
              <Subheading className="text-3xl/10 sm:text-4xl/12">
                Get a tailored read of your operations.
              </Subheading>
              <Text size="lg" className="max-w-2xl">
                30 minutes with our team, not a demo. We'll build a simulation
                around your real numbers, walk through the
                highest-coordination-cost operations, and hand you a one-page
                plan for what to automate first.
              </Text>
              <Button
                className="btn-primary nb-talk-btn"
                id="nb-talk-cta"
                size="lg"
              >
                Book a walkthrough →
              </Button>
            </section>

            {/* Portfolio rollup — moved to the very bottom of the page
                per the May 2026 review. This block answers a different
                question than the rest of Sandbox ("across an org like
                yours, what would Planapus reclaim across ~10 typical
                operations?" vs the per-process numbers above). Putting
                it at the end with its own eyebrow makes the shift in
                scope explicit. */}
            <section className="nb-section nb-portfolio flex flex-col gap-6" id="nb-portfolio">
              <Eyebrow className="nb-eyebrow">Zoom out</Eyebrow>
              <Subheading className="text-3xl/10 sm:text-4xl/12">
                Now scale this beyond one use case.
              </Subheading>
              <Text className="nb-section-sub">
                Planapus runs the same coordination substrate across every
                multi-party operation. The numbers below sum what Planapus
                would reclaim across ten typical operations in an org like
                yours — separate from the per-use-case figures above.
              </Text>
              <SandboxPortfolioWins proc={proc} />
            </section>
          </Container>
        </article>
      </section>
      <SandboxFloatingFab hasProcess={!!proc} onClick={openChangeModal} />
      <SandboxSceneDots />
    </>
  );
}

